import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { LockIcon, RepeatIcon, ShieldCheckIcon } from 'lucide-react';
import { Drawer } from '../shared/Drawer';
import { useCart, useOverlayActions, useOverlays } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { GiftProgress } from './GiftProgress';
import { CartLine } from './CartLine';
import { CartGifts } from './CartGifts';
import { CartCode, CartRecommendations } from './CartExtras';
import { CartAdjustedNotice } from './CartAdjustedNotice';
import { countSoldOut } from '../../core/rules/stock';
import { btnPrice, btnPriceAmount, btnPriceLabel, btnPrimary, btnSecondary } from '../../ui/styles';
import { euroCents } from './cartRules';
import { track } from '../../utils/analytics';

/** De winkelmand: producten in de hoofdrol, cadeaubalk erboven, totaal en afrekenen onderaan. */
export function CartDrawer() {
  const {
    cartOpen
  } = useOverlays();
  const {
    closeCart,
    openQuiz
  } = useOverlayActions();
  const {
    lines,
    subtotal,
    giftSubtotal,
    shipping,
    total,
    gifts,
    nextGift,
    giftTiers,
    hasSubscription
  } = useCart();
  const navigate = useNavigate();
  const {
    pathname
  } = useLocation();
  const inAccount = pathname === '/account' || pathname.startsWith('/account/');
  const count = lines.reduce((n, l) => n + l.qty, 0);
  const soldOutCount = countSoldOut(lines.map(l => l.product));
  const footer = lines.length > 0 ? <div data-id="element-6658" data-mp="2jlfwk6v2rqj">
        <dl className="space-y-1.5 text-[14px] text-muted" data-id="element-6659" data-mp="2jbrh11gssmy">
          <div className="flex justify-between" data-id="element-6660" data-mp="3jt098ctsraed">
            <dt data-id="element-6661" data-mp="3js8j3hiyy4vh">Subtotaal</dt>
            <dd className="text-ink" data-id="element-6662" data-mp="3orustgzl92zj">{euroCents(subtotal)}</dd>
          </div>
          <div className="flex justify-between" data-id="element-6663" data-mp="3oq09u87vnky3">
            <dt data-id="element-6664" data-mp="28dbozt7xgph9">Verzending</dt>
            <dd className={shipping === 0 ? 'text-clay' : 'text-ink'} data-id="element-6665" data-mp="28c1nsv5s316e">{shipping === 0 ? 'Gratis' : euroCents(shipping)}</dd>
          </div>
        </dl>

        {/* Uitverkocht in de mand: hier al zeggen, niet pas bij afrekenen tegen een geblokkeerde knop aanlopen. */}
        {soldOutCount > 0 && <p className="mt-4 text-center text-[13px] text-clay" id="cart-blocked" data-id="element-6666" data-mp="eve2h9a57jjv">
            Haal eerst {soldOutCount === 1 ? 'het uitverkochte product' : 'de uitverkochte producten'} weg.
          </p>}
        <button type="button" disabled={soldOutCount > 0} aria-describedby={soldOutCount > 0 ? 'cart-blocked' : undefined} onClick={() => {
      // Vóór het wegnavigeren: op /afrekenen zelf telt niets mee.
      track('afrekenen-gestart', {});
      closeCart();
      navigate('/afrekenen');
    }} className={`${btnPrice} mt-4`} data-id="element-6667" data-mp="52ylofal110k">
          {/* Zelfde vorm als elke koopknop: de actie links, het totaal rechts (incl. verzending). */}
          <span className={btnPriceLabel} data-id="element-6668" data-mp="1gk82eu2btey6">
            <LockIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-6669" data-mp="1gl9d2xffjzfd" />
            Veilig afrekenen
          </span>
          <span className={btnPriceAmount} data-id="element-6670" data-mp="1o1o3z4ohgndn">
            <span className="sr-only" data-id="element-6671" data-mp="1t2231x83wmzj">Totaal </span>
            {euroCents(total)}
          </span>
        </button>
        {/* Vanuit Mijn omgeving (bijv. "Opnieuw bestellen"): zelf kiezen of je weggaat. */}
        {inAccount && <button type="button" onClick={closeCart} className={`${btnSecondary} mt-2 w-full`} data-id="element-6672" data-mp="22s3tuwrugji5">
            Verder in Mijn omgeving
          </button>}
        <p className="mt-3 flex items-center justify-center gap-2 text-center text-[13px] text-muted" data-id="element-6673" data-mp="1a3g1kh8ogtv3">
          <ShieldCheckIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6674" data-mp="1f2syjut9jhbe" />
          {policyCopy.returnsShort} · {policyCopy.payments}
        </p>
      </div> : undefined;
  return <Drawer open={cartOpen} onClose={closeCart} title={count > 0 ? `Winkelmand (${count})` : 'Winkelmand'} footer={footer} data-id="element-6675" data-mp="2qj0w1htu82g7">
      {lines.length === 0 ? <div className="flex h-full flex-col items-center justify-center text-center" data-id="element-6676" data-mp="2vj5c56qu2v5w">
          <CartAdjustedNotice className="mb-8 text-left" data-id="element-6677" data-mp="2t4qebakjrtvw" />
          <p className="font-display text-2xl text-ink" data-id="element-6678" data-mp="2p23rxp6nb163">Je winkelmand is nog leeg</p>
          <p className="mt-3 max-w-xs text-[15px] leading-relaxed text-muted" data-id="element-6679" data-mp="3313i519t3o4d">
            Weet je al wat je zoekt? Blader door de collectie. Twijfel je, dan wijs ik je in één minuut de juiste kant op.
          </p>
          <Link to="/webshop" onClick={closeCart} className={`${btnPrimary} mt-6`} data-id="element-6680" data-mp="3c0gjt7rov8ej">
            Naar de producten
          </Link>
          <button type="button" onClick={() => {
        closeCart();
        openQuiz();
      }} aria-haspopup="dialog" className="mt-3 px-2 py-2 text-[14px] text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-6681" data-mp="37xtwnk5yqbno">
            Of laat {founder.firstName} meekijken
          </button>
        </div> : <>
          <CartAdjustedNotice className="mb-4" data-id="element-6682" data-mp="e6aayz00v0bx" />
          <GiftProgress subtotal={giftSubtotal} tiers={giftTiers} next={nextGift} data-id="element-6683" data-mp="e7l8wi6sddv4" />

          {/* Producten — de hoofdrol */}
          <ul className="mt-2 divide-y divide-line" data-id="element-6684" data-mp="48mbjvrh14nf">
            <AnimatePresence initial={false} data-id="element-6685" data-mp="4847icykhm4y">
              {lines.map(line => <CartLine key={line.product.id} line={line} data-id="element-6686" data-mp="96nwyncddg22" />)}
            </AnimatePresence>
          </ul>
          <CartGifts gifts={gifts} data-id="element-6687" data-mp="3qmnis0s8wl77" />

          {hasSubscription && <p className="mt-2 flex items-start gap-2.5 bg-blush p-4 text-[13px] leading-relaxed text-ink" data-id="element-6688" data-mp="3vfyuogbwzs5z">
              <RepeatIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6689" data-mp="3hotqd0xlqbpb" />
              Je abonnement start met deze levering. Pauzeren of stoppen kan altijd in één klik.
            </p>}

          <CartRecommendations data-id="element-6690" data-mp="37rfk1u5e73aq" />
          <CartCode data-id="element-6691" data-mp="3coz111th5u6d" />
        </>}
    </Drawer>;
}